import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import FONASUAllocationInput from '../FONASUAllocationInput/FONASUAllocationInput';
import { CartContext } from '../../context/CartContext/CartContext';
import { checkPortfolioMinValidations } from './util';
import {
  checkIClassValidation,
  checkSummitIClassValidation
} from '../AllocationTable/utils';
import {
  addCommas,
  I_CLASS_MINIMUM,
  toThousands
} from '../../flows/shared/utils';
import selectFundsRibbon from '../../images/select-funds-ribbon.svg';
import warningIcon from '../../images/icon-warning.svg';
import { MFBeaconButton } from '../Beacon/BeaconComponents';

import styles from './FONASUAllocationTable.css';

const renderModeBtn = (val, selectedFunds, active, onClick) =>
  active || !selectedFunds ? (
    val
  ) : (
    <a
      className="toggle-link"
      href="#"
      onClick={e => {
        e.preventDefault();
        onClick();
      }}>
      {val}
      <style jsx>{styles}</style>
    </a>
  );

const IClassMinimumValidation = ({ amount, iClassReward }) => {
  const iClassMinWithCommas = addCommas(I_CLASS_MINIMUM);
  return iClassReward
    ? checkSummitIClassValidation(amount) && (
        <>
          <div className="validation-msg">
            <img
              className="validation-msg-icon"
              src={warningIcon}
              alt="warning"
            />
            A $50,000 minimum is required for I Class
          </div>
          <style jsx>{styles}</style>
        </>
      )
    : checkIClassValidation(amount) && (
        <>
          <div className="minimum-validation">
            <div>
              Allocation does not meet the minimum investment amount of $
              {iClassMinWithCommas} per fund.
            </div>
          </div>
          <style jsx>{styles}</style>
        </>
      );
};

const FONASUAllocationTable = ({
  fundsWithCategories = [],
  readOnly = false,
  taxableJointPortfolios,
  taxableIndividualPortfolios,
  rewardsAvailable = { closedFunds: false, iClassFunds: false },
  matchingAgreementArray = [],
  accountType
}) => {
  const cartState = useContext(CartContext);
  const {
    percentMode,
    totalAmount,
    percentSum,
    amountSum,
    requiredTotalText,
    isValidTotal,
    api: { setPercentMode, removeFund, changeValue }
  } = cartState;

  const selectedFunds = fundsWithCategories.length;

  const minValidation = (fund, minInvAmtError) => {
    const minInvAmtErrorWithCommas = addCommas(minInvAmtError);
    return fund.iClass ? (
      <IClassMinimumValidation
        amount={fund.amount}
        iClassReward={rewardsAvailable.iClassFunds}
      />
    ) : (
      <>
        <div className="minimum-validation">
          {minInvAmtError && (
            <div>
              Allocation does not meet the minimum investment amount of $
              {minInvAmtErrorWithCommas} per fund.
            </div>
          )}
        </div>
        <style jsx>{styles}</style>
      </>
    );
  };

  return (
    <div className="allocation-table">
      <div className="headers">
        <span>Fund Name</span>
        <span>
          Allocation:{' '}
          {renderModeBtn('%', selectedFunds, percentMode, () =>
            setPercentMode(true)
          )}{' '}
          |{' '}
          {renderModeBtn('$', selectedFunds, !percentMode, () =>
            setPercentMode(false)
          )}
        </span>
      </div>

      <div className="funds">
        {fundsWithCategories.length > 0 &&
          fundsWithCategories.map(fund => {
            const { stocks, bonds, shortTermReserves } = fund.allocation;
            const minInvAmtError = checkPortfolioMinValidations(
              fund,
              taxableJointPortfolios,
              taxableIndividualPortfolios
            );
            return (
              <div
                className="fund-row"
                key={`${fund.ticker}-${fund?.agreementKey?.uniqueKey}`}>
                <div
                  className={`fund-details${
                    readOnly ? ' fund-details-readonly' : ''
                  }`}>
                  <div className="name">
                    {fund?.selectFund && (
                      <img
                        className="allo-sf-ribbon-icon"
                        src={selectFundsRibbon}
                        alt="Select Fund Certified"
                      />
                    )}
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="additonal-fund-details"
                      href={`https://www.troweprice.com/personal-investing/tools/fund-research/${fund.ticker}`}>
                      {fund.name} <span className="ticker">{fund.ticker}</span>
                    </a>
                  </div>

                  <div className="allocation">
                    <span className="icon stocks-icon" title="Stocks" />
                    <span className="icon-description"> {stocks}% </span>
                    <span className="icon bonds-icon" title="Bonds" />
                    <span className="icon-description"> {bonds}% </span>
                    <span className="icon short-term-icon" title="Short-term" />
                    <span className="icon-description">
                      {' '}
                      {shortTermReserves}%{' '}
                    </span>
                  </div>
                </div>
                {readOnly || (
                  <div className="remove-button">
                    <MFBeaconButton
                      id="fonasuRemoveFundBtn"
                      data-name={`fonasuRemoveFundBtn-${fund.ticker}`}
                      type="button"
                      onClick={() => removeFund(fund.ticker)}
                      variant="tertiary"
                      label="Remove"
                    />
                  </div>
                )}
                <FONASUAllocationInput
                  percentMode={percentMode}
                  onChange={value => changeValue(fund.ticker, value)}
                  percent={fund.percent}
                  dollarValue={fund.amount}
                  totalAmount={totalAmount}
                  readOnly={readOnly}
                  accountType={accountType}
                />
                {minValidation(fund, minInvAmtError)}
                {matchingAgreementArray.includes(fund.ticker) && (
                  <div className="matching-agreement">
                    <div>Existing Agreement</div>
                  </div>
                )}
              </div>
            );
          })}{' '}
      </div>

      {selectedFunds ? (
        <div className="totals">
          <div className="totals-row">
            <div>Allocation Total of your ${totalAmount}</div>
            <div>
              {percentMode ? `${toThousands(percentSum)}%` : `$${amountSum}`}
            </div>
          </div>

          {isValidTotal || (
            <div className="validation-msg">
              Your allocation must total {requiredTotalText}.{' '}
            </div>
          )}

          <div className="totals-row">
            <div className="allocation">
              <span className="icon-description"> Legend: </span>
              <span className="icon stocks-icon" />
              <span className="icon-description"> STOCKS </span>
              <span className="icon bonds-icon" />
              <span className="icon-description"> BONDS </span>
              <span className="icon short-term-icon" />
              <span className="icon-description"> SHORT-TERM </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-funds">
          <div>You have not added any funds yet.</div>
        </div>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

FONASUAllocationTable.propTypes = {
  fundsWithCategories: PropTypes.arrayOf(
    PropTypes.shape({
      ticker: PropTypes.string,
      name: PropTypes.string,
      percent: PropTypes.number,
      amount: PropTypes.number,
      allocation: PropTypes.shape({
        stocks: PropTypes.number,
        bonds: PropTypes.number,
        shortTermReserves: PropTypes.number
      }),
      meetsMinimumInvestmentAmount: PropTypes.bool,
      meetsAccountMinimumOpenIRA: PropTypes.bool
    })
  ),
  readOnly: PropTypes.bool
};

export default FONASUAllocationTable;
